import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { csrfFetch } from '../utils/csrfFetch';

/**
 * ProjectContext - Manages project state across the application (SAT-64)
 *
 * Provides:
 * - selectedProject: The currently selected project (null = All Projects)
 * - projects: List of all projects
 * - setSelectedProject: Function to change selected project
 * - refreshProjects: Function to reload projects from API
 * - updateProject: Function to update a project in the list
 * - getProjectCustomInstructions: Get custom instructions for the selected project
 */

const ProjectContext = createContext(null);

export function ProjectProvider({ children }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch projects from API
  const refreshProjects = useCallback(async () => {
    try {
      const response = await csrfFetch('/api/projects', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load projects on mount
  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  // Update a project in the list (called after editing)
  const updateProject = useCallback((updatedProject) => {
    setProjects(prev =>
      prev.map(p => p.id === updatedProject.id ? updatedProject : p)
    );
    // Also update selected project if it's the one being updated
    if (selectedProject?.id === updatedProject.id) {
      setSelectedProject(updatedProject);
    }
  }, [selectedProject]);

  // Get custom instructions for the currently selected project
  const getProjectCustomInstructions = useCallback(() => {
    if (!selectedProject) return null;
    return selectedProject.custom_instructions || null;
  }, [selectedProject]);

  // Build system prompt with project instructions
  const buildSystemPrompt = useCallback((basePrompt = '') => {
    const customInstructions = getProjectCustomInstructions();

    if (!customInstructions) {
      return basePrompt || 'You are DP, an AI assistant built for dealership staff. You help Service Advisors, Technicians, and Managers with daily operations: repair order workflows, shop productivity, customer communication, parts and inventory, compliance, and business insights. Keep responses practical and relevant to automotive dealership operations. Avoid technical or software development topics unless specifically asked.\n\nWhen displaying results from SRM API tools, never show UUIDs, account IDs, or internal reference codes to the user. Present data using human-readable names, descriptions, and values only (e.g. show "Decisiv Service Center" not "701e1d80-b9ef-11e7-961e-0a6ace0b0cae" or "ZG2-266"). You may use IDs internally for follow-up tool calls but keep them out of your visible responses.';
    }

    // Prepend project instructions to the base prompt
    const projectInstructionsPrefix = `[Project-Specific Instructions]\n${customInstructions}\n\n[End Project Instructions]\n\n`;

    if (basePrompt) {
      return projectInstructionsPrefix + basePrompt;
    }

    return projectInstructionsPrefix + 'You are DP, an AI assistant built for dealership staff. You help Service Advisors, Technicians, and Managers with daily operations: repair order workflows, shop productivity, customer communication, parts and inventory, compliance, and business insights. Keep responses practical and relevant to automotive dealership operations. Avoid technical or software development topics unless specifically asked.\n\nWhen displaying results from SRM API tools, never show UUIDs, account IDs, or internal reference codes to the user. Present data using human-readable names, descriptions, and values only (e.g. show "Decisiv Service Center" not "701e1d80-b9ef-11e7-961e-0a6ace0b0cae" or "ZG2-266"). You may use IDs internally for follow-up tool calls but keep them out of your visible responses.';
  }, [getProjectCustomInstructions]);

  const value = {
    selectedProject,
    setSelectedProject,
    projects,
    isLoading,
    refreshProjects,
    updateProject,
    getProjectCustomInstructions,
    buildSystemPrompt
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}

export default ProjectContext;
